// TODO: Maybe we should make this a 'server-only' file?

const defaultRequestOptions: Omit<RequestInit, 'cache' | 'next'> = {
  headers: {
    'Content-Type': 'application/json',
  },
  method: 'POST',
};

const businessRequestOptions: Omit<RequestInit, 'cache' | 'next'> = {
  headers: {
    // biome-ignore lint/style/useNamingConvention: Thats how it works
    Authorization: `Basic ${btoa(`${process.env.BUSINESS_USERNAME}:${process.env.BUSINESS_PASSWORD}`)}`,
    'Content-Type': 'application/json',
  },
  method: 'GET',
};

const vodVideoRequestOptions: Omit<RequestInit, 'cache' | 'next'> = {
  headers: {
    'X-Api-Key': `${process.env.VOD_API_KEY}`,
  },
  method: 'GET',
};

type Response<T> = { success: true; value: T } | { success: false; error: Error };

/**
 * Get the options for the request by merging the default request options with the incoming request options
 * Allows for overriding the default request options with the incoming request options without having to rewrite the whole object
 * @param requestOptions - The request options
 * @returns The options for the request
 */
function getOptions(requestOptions: Omit<RequestInit, 'cache' | 'next'> = {}) {
  const { headers: headersFromRequestOptions, ...restRequestOptions } = requestOptions;
  const { headers: defaultHeaders, ...restDefaultRequestOptions } = defaultRequestOptions;

  return {
    headers: { ...defaultHeaders, ...headersFromRequestOptions },
    ...restDefaultRequestOptions,
    ...restRequestOptions,
  };
}

async function fetchData(
  endpoint: string,
  requestOptions: Omit<RequestInit, 'cache' | 'next'>,
  nextRequestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
  body?: object,
  type: 'json' | 'text' = 'json',
) {
  const options = getOptions(requestOptions);

  const fetchOptions = {
    ...nextRequestOptions,
    ...options,
  };
  if (fetchOptions.method === 'POST') {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(endpoint, fetchOptions);

  if (!response.ok) {
    throw new Error(`HTTP Error: ${response.status}`);
  }

  switch (type) {
    case 'json': {
      const json = await response.json();
      if (json.errors) {
        throw new Error(`[fetchData] Error: ${JSON.stringify(json.errors)}`);
      }
      return json;
    }
    case 'text':
      return await response.text();
    default:
      throw new Error(`Unknown type: ${type}`);
  }
}

// NOTE: Switch this to use exceptions pattern rather than results objects
// biome-ignore lint/complexity/noStaticOnlyClass: <We need just these functions>
export class GetData {
  private static async getData<T>(
    query: string,
    params: object,
    endpoint: string,
    nextRequestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
    requestOptions: Omit<RequestInit, 'cache' | 'next'> = {},
  ): Promise<Response<T>> {
    try {
      const useGet = requestOptions.method === 'GET';
      const url = useGet
        ? `${endpoint}?query=${encodeURIComponent(query)}&variables=${encodeURIComponent(JSON.stringify(params))}`
        : endpoint;
      const json = await fetchData(
        url,
        requestOptions,
        nextRequestOptions,
        useGet ? undefined : { query, variables: params },
      );

      if (!json.data) {
        return { error: { message: 'No Data returned from GraphQL Query', name: 'No Data' }, success: false };
      }

      return { success: true, value: json.data };
    } catch (error) {
      return {
        error: { message: `Unknown getData error: ${error?.toString()}`, name: 'Unknown Error' },
        success: false,
      };
    }
  }

  private static async getDataRest<T>(
    endpoint: string,
    body?: object,
    nextRequestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
    requestOptions: Omit<RequestInit, 'cache' | 'next'> = {},
    type: 'json' | 'text' = 'json',
  ): Promise<Response<T>> {
    try {
      const data = await fetchData(endpoint, requestOptions, nextRequestOptions, body, type);

      if (!data) {
        return { error: { message: 'No Data returned from REST Api', name: 'No Data' }, success: false };
      }

      return { success: true, value: data };
    } catch (error) {
      return {
        error: { message: `Unknown getDataRest error: ${error?.toString()}`, name: 'Unknown Error' },
        success: false,
      };
    }
  }

  static async content<T>(
    query: string,
    params: object = {},
    nextRequestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
    requestOptions: Omit<RequestInit, 'cache' | 'next'> = {},
  ): Promise<Response<T>> {
    return await GetData.getData<T>(
      query,
      params,
      `${process.env.WORDPRESS_URL}${process.env.WORDPRESS_GQL_PATH}`!,
      nextRequestOptions,
      requestOptions,
    );
  }

  static async backend<T>(
    query: string,
    params: object = {},
    nextRequestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
    requestOptions: Omit<RequestInit, 'cache' | 'next'> = {},
  ): Promise<Response<T>> {
    return await GetData.getData<T>(
      query,
      params,
      (process.env.REPLATFORM_GQL_URL_OVERRIDE || process.env.REPLATFORM_GQL_URL)!,
      nextRequestOptions,
      requestOptions,
    );
  }

  static async redVentures<T>(
    query: string,
    params: object,
    requestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
  ): Promise<Response<T>> {
    return await GetData.getData<T>(query, params, process.env.RED_VENTURES_GQL_URL!, requestOptions);
  }

  static async business<T>(
    endpoint: string,
    body: object = {},
    nextRequestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
    requestOptions: Omit<RequestInit, 'cache' | 'next'> = businessRequestOptions,
  ): Promise<Response<T>> {
    // Making a legacy check to not break the build
    let url = process.env.BUSINESS_URL;
    if (url?.includes('/v1')) {
      url = url.replace('/v1', '');
    }

    return await GetData.getDataRest<T>(`${url}${endpoint}`, body, nextRequestOptions, requestOptions);
  }

  static async vodVideo<T>(
    endpoint: string,
    type: 'json' | 'text' = 'json',
    nextRequestOptions: Pick<RequestInit, 'cache' | 'next'> = {},
    requestOptions: Omit<RequestInit, 'cache' | 'next'> = vodVideoRequestOptions,
  ): Promise<Response<T>> {
    return await GetData.getDataRest<T>(
      `${process.env.VOD_BASE_URL}${endpoint}`,
      {},
      nextRequestOptions,
      requestOptions,
      type,
    );
  }
}
